/**
 * The Profil tab: who the trainer is to their clients, and the password they sign in with.
 *
 * Three blocks in the mockup's order, drawn in the sheet's `.tight` cut — this tab has to fit a
 * 1280x720 laptop with no scroll, and the looser inset costs forty pixels it does not have.
 *
 * It owns no state: everything arrives on one object from `lib/useAccountProfile`, whose read also
 * feeds the card in the column beside it. A tab that made the read itself would make it twice.
 *
 * „Usuń konto" is a text button rather than a danger-filled one — the control is a way into a
 * question, not the answer to it, and red here would be the loudest thing on the tab.
 *
 * A refused control is `aria-disabled`, never the attribute: it has to stay in the tab order for
 * its reason to be reachable (view ADR §12), and the reason is RENDERED and pointed at with
 * `aria-describedby` rather than hidden in a `title`.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { PasswordCriteria } from '~/features/auth-by-email';
import { BrandLoader } from '~/shared/ui/BrandLoader';
import { LockIcon } from '~/shared/ui/AuthIcons';
import { FormBanner } from '~/shared/ui/FormBanner';
import { FormField } from '~/shared/ui/FormField';
import { PasswordField } from '~/shared/ui/PasswordField';
import { displayInitials } from '~/shared/lib/displayInitials';
import ink from '~/shared/ui/inkButton.module.css';
import photoBox from '~/shared/ui/photoBox.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { AccountProfileApi } from '../lib/useAccountProfile';
import { AVATAR_ACCEPT } from '../lib/avatarFile';
import { CameraIcon } from './AccountIcons';
import screen from './AccountScreen.module.css';
import styles from './ProfileTab.module.css';

/** The four rules, in the mockup's order, against upstream's own words. */
const RULES = [
  { key: 'length', labelKey: 'auth.pw.len' },
  { key: 'uppercase', labelKey: 'auth.pw.upper' },
  { key: 'digit', labelKey: 'auth.pw.num' },
  { key: 'special', labelKey: 'auth.pw.spec' },
] as const satisfies readonly { key: keyof PasswordCriteria; labelKey: string }[];

interface ProfileTabProps {
  readonly profile: AccountProfileApi;
  /** Opens the erase-account dialog. */
  readonly onRequestErase: () => void;
  /**
   * The screen holds this so the dialog can hand focus BACK to the control that raised it. The
   * link is still mounted when the dialog closes, so this is the ordinary return rather than a
   * repair — but naming it here is what keeps it that way if the foot of this tab ever moves.
   */
  readonly eraseRef: React.RefObject<HTMLButtonElement | null>;
}

export function ProfileTab({ profile, onRequestErase, eraseRef }: ProfileTabProps) {
  const { t } = useTranslation();
  const saveRefusalId = useId();
  const passwordRefusalId = useId();

  // The READ, deliberately, and NOT the section's gated `waiting`: this block draws no skeleton
  // bars — the form's own boxes are the reserve — so there is nothing here that outlives the
  // answer. `aria-busy`, the hidden sentence and `readOnly` all settle when the values arrive.
  const reading = profile.state === 'pending';
  const failed = profile.state === 'error';
  /* The row exists when either control inside it does — and while a refusal is showing, because the
   * banner under it stands on this row's own top margin. */
  const photoActions =
    profile.photoError !== null || profile.onRetryPhoto !== null || profile.avatarUrl !== null;

  return (
    <div className={`${String(screen.inner)} ${String(screen.tight)}`}>
      <div className={screen.lede}>
        <h2 className={screen.h2}>{t('web:account.profile.heading')}</h2>
        <p className={screen.ledeText}>{t('web:account.profile.lede')}</p>
      </div>

      {/* No skeleton bars in this block, and that is the loading policy honoured rather than
        * skipped: a form's boxes ARE the reserve. The three fields are laid out identically in all
        * three states and only their contents arrive, so the answer landing changes ink and never
        * geometry — which is the property bars exist to buy on a card with no fixed shape.
        * `aria-busy` and one hidden sentence are what a listener gets in the bars' place. The card
        * in the column beside this one does draw them, because a 30px letter-spaced code appearing
        * in an unreserved line is exactly the case they are for. */}
      <div className={screen.block} aria-busy={reading}>
        {reading && <span className={hidden.srOnly}>{t('web:account.profile.waiting')}</span>}
        {failed && <FormBanner message={t('web:account.profile.loadFailed')} />}

        <div className={styles.who}>
          <div className={styles.photoCell}>
            {/* THREE branches, in the order the trainer meets them. While the photograph is being
              * prepared and sent, the circle itself is what waits — the loader stands where the
              * face will be, so there is one place to look and no second control to interpret. The
              * busy state does not end until `/auth/me` has answered, so the loader hands over to
              * the photograph and never to the initials in between. */}
            {profile.photoBusy ? (
              <span className={`${String(styles.avatar)} ${String(styles.avatarBusy)}`}>
                <BrandLoader label={t('web:account.photo.uploading')} size="panel" />
              </span>
            ) : profile.avatarUrl === null ? (
              <span className={styles.avatar} aria-hidden="true">
                {displayInitials(profile.initials)}
              </span>
            ) : (
              /* A SIBLING branch, never a layer over a layer — `shared/AGENTS.md`'s rule, so
               * `photoBox`'s ground is the placeholder and the error state without two stacking. */
              <img
                className={`${String(photoBox.photo)} ${String(styles.photo)}`}
                src={profile.avatarUrl}
                alt=""
                decoding="async"
              />
            )}

            {/* The label IS the control's box and the input inside it is the control: the platform's
              * own picker opens, and the tab order needs nothing held in place by JavaScript. */}
            <label className={styles.camera}>
              <CameraIcon className={styles.cameraIcon} />
              <span className={hidden.srOnly}>{t('web:account.photo.change')}</span>
              <input
                className={styles.fileInput}
                type="file"
                accept={AVATAR_ACCEPT}
                // Refused, never `disabled`: a disabled input leaves the tab order, so the one
                // keyboard path to the photograph disappears for the length of every upload.
                aria-disabled={profile.photoBusy ? 'true' : undefined}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  // The value is cleared so that picking the SAME file twice — after a failure the
                  // trainer went away and fixed — still fires a change event.
                  event.target.value = '';
                  if (file !== undefined && !profile.photoBusy) profile.onPickPhoto(file);
                }}
              />
            </label>
          </div>

          <FormField
            label={t('web:account.profile.firstName')}
            value={profile.firstName}
            onChange={profile.onFirstNameChange}
            maxLength={50}
            autoComplete="given-name"
            // `readOnly`, never `disabled`: a disabled field leaves the tab order, so the strip's
            // keyboard path changes shape while the read is in flight. The block above carries the
            // `aria-busy` and the reason — the waiting line, or the failure banner.
            readOnly={reading || failed}
          />
          <FormField
            label={t('web:account.profile.lastName')}
            value={profile.lastName}
            onChange={profile.onLastNameChange}
            maxLength={50}
            autoComplete="family-name"
            readOnly={reading || failed}
          />
        </div>

        {photoActions && (
          <div className={styles.photoActions}>
            {profile.avatarUrl !== null && (
              <button
                type="button"
                className={styles.textButton}
                aria-disabled={profile.photoBusy ? 'true' : undefined}
                onClick={profile.photoBusy ? undefined : profile.onRemovePhoto}
              >
                {t('web:account.photo.remove')}
              </button>
            )}
            {/* Offered only when there are prepared bytes to send again — a network failure must not
              * make the trainer find the file a second time. */}
            {profile.onRetryPhoto !== null && (
              <button type="button" className={styles.textButton} onClick={profile.onRetryPhoto}>
                {t('web:account.photo.retry')}
              </button>
            )}
          </div>
        )}
        <FormBanner message={profile.photoError} />

        <FormField
          label={t('web:account.profile.email')}
          value={profile.email}
          onChange={() => {
            // Read-only: the backend refuses `email` outright (`email_change_unsupported`), so this
            // field has no writer at all rather than one that is turned off.
          }}
          type="email"
          readOnly
          trailing={<LockIcon className={screen.rowIcon} />}
        />

        <div className={styles.foot}>
          <span className={styles.footNote}>{t('web:account.profile.emailHint')}</span>
          {!profile.saveIsOffered && (
            <span id={saveRefusalId} className={hidden.srOnly}>
              {t('web:account.profile.saveRefusal')}
            </span>
          )}
          <button
            type="button"
            className={`${String(ink.ink)} ${String(screen.accountButton)} ${String(screen.accountButtonBold)} ${String(styles.save)}`}
            aria-disabled={profile.saveIsOffered ? undefined : 'true'}
            aria-describedby={profile.saveIsOffered ? undefined : saveRefusalId}
            onClick={profile.saveIsOffered ? profile.onSave : undefined}
          >
            {t('web:account.profile.save')}
          </button>
        </div>
        <FormBanner message={profile.saveError} />
      </div>

      <div className={screen.block}>
        <h3 className={screen.blockTitle}>{t('web:account.password.heading')}</h3>

        <div className={styles.pair}>
          <PasswordField
            label={t('web:account.password.new')}
            value={profile.newPassword}
            onChange={profile.onNewPasswordChange}
            autoComplete="new-password"
          />
          <PasswordField
            label={t('web:account.password.repeat')}
            value={profile.repeatPassword}
            onChange={profile.onRepeatPasswordChange}
            autoComplete="new-password"
            // The ring with no words of its own: the reason is the rules beside the button, and a
            // per-field message would say the same thing twice.
            invalid={profile.passwordAssessment.confirmation === 'mismatch'}
          />
        </div>

        <div className={styles.foot}>
          <ul className={styles.rules}>
            {RULES.map((rule) => {
              const met = profile.passwordAssessment.criteria[rule.key];
              return (
                <li
                  key={rule.key}
                  className={`${String(styles.rule)} ${met ? String(styles.ruleMet) : ''}`}
                  // The state reaches assistive technology through the ROLE rather than through
                  // words this app does not have a translation for — the platform announces
                  // "checked" in the reader's own language.
                  role="checkbox"
                  aria-checked={met}
                  aria-disabled="true"
                >
                  <span className={styles.ruleMark} aria-hidden="true" />
                  {t(rule.labelKey)}
                </li>
              );
            })}
          </ul>
          {!profile.passwordSaveIsOffered && (
            <span id={passwordRefusalId} className={hidden.srOnly}>
              {t('web:account.password.refusal')}
            </span>
          )}
          <button
            type="button"
            className={`${String(ink.ink)} ${String(screen.accountButton)} ${String(screen.accountButtonBold)} ${String(styles.save)}`}
            aria-disabled={profile.passwordSaveIsOffered ? undefined : 'true'}
            aria-describedby={profile.passwordSaveIsOffered ? undefined : passwordRefusalId}
            onClick={profile.passwordSaveIsOffered ? profile.onSavePassword : undefined}
          >
            {t('web:account.password.save')}
          </button>
        </div>
        <FormBanner message={profile.passwordError} />
      </div>

      <div className={screen.block}>
        <button
          ref={eraseRef}
          type="button"
          className={`${String(styles.textButton)} ${String(styles.erase)}`}
          onClick={onRequestErase}
        >
          {t('web:account.profile.erase')}
        </button>
      </div>
    </div>
  );
}
